import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Stack,
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  DirectionsBoat as BoatIcon,
  Inventory as CargoIcon,
  Inventory2 as ContainerIcon,
  Engineering as ProjectIcon,
  Science as LabIcon,
  HelpOutline as OtherIcon,
  ArrowForward as NextIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import API_BASE_URL from '../config';
import Seo from '../components/Seo';
import { trackEvent, trackLead } from '../utils/analytics';

const serviceOptions = [
  { key: 'Marine & Draft Surveys', icon: <BoatIcon /> },
  { key: 'General & Liquid Cargo', icon: <CargoIcon /> },
  { key: 'Container Inspections', icon: <ContainerIcon /> },
  { key: 'Project & Over-Dimensional', icon: <ProjectIcon /> },
  { key: 'Laboratory Testing', icon: <LabIcon /> },
  { key: 'Other / Not sure', icon: <OtherIcon /> },
];

const timelines = ['Urgent (within 48 hours)', 'This week', 'This month', 'Just planning ahead'];

const steps = ['Service', 'Details', 'Your Info'];

const QuoteRequest = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [form, setForm] = useState({
    service: '',
    location: '',
    cargoType: '',
    timeline: '',
    name: '',
    company: '',
    email: '',
    phone: '',
    details: '',
  });

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  // Track funnel entry once
  useEffect(() => {
    trackEvent('quote_start');
  }, []);

  const goNext = () => {
    const stepName = steps[activeStep];
    trackEvent('quote_step_complete', { step_number: activeStep + 1, step_name: stepName });
    if (activeStep === 0) trackEvent('quote_select_service', { service: form.service });
    setActiveStep((s) => s + 1);
  };

  const canProceed = () => {
    if (activeStep === 0) return !!form.service;
    if (activeStep === 1) return !!form.location && !!form.timeline;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const message = [
      `Quote request via website`,
      `Service: ${form.service}`,
      `Location / Port: ${form.location}`,
      `Cargo type: ${form.cargoType || 'N/A'}`,
      `Timeline: ${form.timeline}`,
      `Company: ${form.company || 'N/A'}`,
      ``,
      `Details: ${form.details || 'None provided'}`,
    ].join('\n');

    try {
      const response = await fetch(`${API_BASE_URL}/contact.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setSnackbar({ open: true, message: result.message || 'Thank you! We will get back to you shortly.', severity: 'success' });
        trackLead('quote', { service: form.service, timeline: form.timeline, location: form.location });
        setActiveStep(3); // success state
      } else {
        setSnackbar({ open: true, message: result.message || 'Something went wrong. Please try again.', severity: 'error' });
        trackEvent('form_error', { form: 'quote' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Network error. Please check your connection and try again.', severity: 'error' });
      trackEvent('form_error', { form: 'quote', error: 'network' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f8fa', py: { xs: 5, md: 8 }, minHeight: '70vh' }}>
      <Seo
        title="Request a Quote"
        description="Request a tailored inspection quote from InspectIQ. Select your service, location and cargo type and our team will respond with a clear scope and price."
        path="/quote"
      />
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="overline" sx={{ color: 'secondary.dark' }}>Get a Quote</Typography>
          <Typography variant="h2" gutterBottom>Tell us what you need inspected</Typography>
          <Typography variant="body1" sx={{ maxWidth: 560, mx: 'auto' }}>
            A few quick details help us send you an accurate scope and quote, fast.
          </Typography>
        </Box>

        <Card sx={{ p: { xs: 3, md: 5 } }}>
          {activeStep < 3 && (
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {/* Step 1: Service */}
          {activeStep === 0 && (
            <Grid container spacing={2}>
              {serviceOptions.map((opt) => {
                const selected = form.service === opt.key;
                return (
                  <Grid item xs={12} sm={6} key={opt.key}>
                    <Card
                      onClick={() => update('service', opt.key)}
                      sx={{
                        p: 2.5,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'transparent',
                        bgcolor: selected ? 'rgba(1,86,122,0.05)' : 'white',
                        transition: 'all 0.15s',
                      }}
                    >
                      <Box sx={{ color: 'primary.main', display: 'flex' }}>{opt.icon}</Box>
                      <Typography sx={{ fontWeight: 600, color: 'text.primary', flexGrow: 1 }}>{opt.key}</Typography>
                      {selected && <CheckIcon sx={{ color: 'primary.main' }} />}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Step 2: Details */}
          {activeStep === 1 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Location / Port"
                  placeholder="e.g. Mundra Port, Gujarat"
                  value={form.location}
                  onChange={(e) => update('location', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Cargo / Commodity type"
                  placeholder="e.g. Bagged soybean meal, chemicals, machinery"
                  value={form.cargoType}
                  onChange={(e) => update('cargoType', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  select
                  label="When do you need it?"
                  value={form.timeline}
                  onChange={(e) => update('timeline', e.target.value)}
                >
                  {timelines.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Anything else we should know? (optional)"
                  value={form.details}
                  onChange={(e) => update('details', e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {/* Step 3: Contact info */}
          {activeStep === 2 && (
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Full Name" value={form.name} onChange={(e) => update('name', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Company" value={form.company} onChange={(e) => update('company', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required type="email" label="Email" value={form.email} onChange={(e) => update('email', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <Card sx={{ p: 2.5, bgcolor: '#f5f8fa' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>Summary</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {form.service && <Chip size="small" label={form.service} color="primary" />}
                    {form.location && <Chip size="small" label={form.location} variant="outlined" />}
                    {form.cargoType && <Chip size="small" label={form.cargoType} variant="outlined" />}
                    {form.timeline && <Chip size="small" label={form.timeline} variant="outlined" />}
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Success */}
          {activeStep === 3 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
              <Typography variant="h4" gutterBottom>Request received</Typography>
              <Typography variant="body1" sx={{ mb: 4, maxWidth: 460, mx: 'auto' }}>
                Thanks{form.name ? `, ${form.name.split(' ')[0]}` : ''}. Our team will review your
                request and get back to you with a scope and quote shortly.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button variant="contained" onClick={() => navigate('/')}>Back to Home</Button>
                <Button variant="outlined" onClick={() => navigate('/services')}>Browse Services</Button>
              </Stack>
            </Box>
          )}

          {/* Controls */}
          {activeStep < 3 && (
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                disabled={activeStep === 0}
                onClick={() => setActiveStep((s) => s - 1)}
              >
                Back
              </Button>
              {activeStep < 2 ? (
                <Button variant="contained" endIcon={<NextIcon />} disabled={!canProceed()} onClick={goNext}>
                  Continue
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || !form.name || !form.email || !form.phone}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Request'}
                </Button>
              )}
            </Stack>
          )}
        </Card>
      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuoteRequest;
